/**
 * Right-click context menu for a shape on the facility-map canvas.
 *
 * Rendered at the cursor by YardMapView when a section or feature is
 * right-clicked. Purely presentational: the parent supplies the action
 * callbacks and closes it on outside-click / Escape.
 */

import { useEffect } from "react";
import { Copy, RotateCw, Lock, Unlock, Trash2 } from "lucide-react";

export interface ContextMenuTarget {
  type: "section" | "feature";
  id: string;
  x: number; // client px
  y: number;
}

interface CanvasContextMenuProps {
  target: ContextMenuTarget;
  locked: boolean;
  onDuplicate: () => void;
  onRotate?: () => void; // sections only
  onToggleLock: () => void;
  onDelete: () => void;
  onClose: () => void;
}

function CanvasContextMenu({
  target,
  locked,
  onDuplicate,
  onRotate,
  onToggleLock,
  onDelete,
  onClose,
}: CanvasContextMenuProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const item =
    "flex w-full items-center gap-2 rounded-[var(--radius-sm)] px-2.5 py-1.5 text-left text-xs text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]";

  return (
    <>
      <div
        className="fixed inset-0 z-[65]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        className="fixed z-[66] min-w-[9rem] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-raised)] p-1 shadow-[var(--shadow-xl)]"
        style={{ left: target.x, top: target.y }}
      >
        <button className={item} onClick={onDuplicate}>
          <Copy className="h-3.5 w-3.5 text-[var(--text-tertiary)]" /> Duplicate
        </button>
        {target.type === "section" && onRotate && (
          <button className={item} onClick={onRotate}>
            <RotateCw className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />{" "}
            Rotate 90°
          </button>
        )}
        <button className={item} onClick={onToggleLock}>
          {locked ? (
            <>
              <Unlock className="h-3.5 w-3.5 text-[var(--text-tertiary)]" />{" "}
              Unlock
            </>
          ) : (
            <>
              <Lock className="h-3.5 w-3.5 text-[var(--text-tertiary)]" /> Lock
            </>
          )}
        </button>
        <div className="my-1 h-px bg-[var(--border-subtle)]" />
        <button
          className={`${item} text-[var(--text-danger)]`}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete
        </button>
      </div>
    </>
  );
}

export default CanvasContextMenu;
